/* eslint-disable no-nested-ternary */
import type { IRunScreenerInputQuery } from "components/screener/v2/query/__generated__/RunScreenerInputQuery.generated";
import type { IRunScreenerInputsQuery } from "components/screener/v2/query/__generated__/RunScreenerInputsQuery.generated";
import type { PrimaryIndicatorDataSource } from "components/ux/urlParsers";
import {
  createObjectIdSchema,
  platformVerticalSchema,
  primaryDataSourceSchema,
  screenerSortSpecifierSchema,
  statementTypeSchema,
  tickerSymbolListSchema,
} from "components/ux/urlParsers";
import type { UseQueryStatesReturn } from "nuqs";
import { createLoader, parseAsBoolean, useQueryStates } from "nuqs";
import { startTransition, useRef, useState } from "react";
import { useEffectOnce, useMountedState } from "react-use";
import type { IRunScreenerInput, IScreenerSortSpecifier, ITickerSymbolList } from "types/graphqlTypes";
import { IFinancialsStatementType, IIndicatorDataSource, IPlatformVertical } from "types/graphqlTypes";

const runScreenerInputObjectIdSchema = createObjectIdSchema("rsi");
export const urlSearchParams = {
  vertical: platformVerticalSchema.withDefault(IPlatformVertical.Acoes),
  primaryDataSource: primaryDataSourceSchema.withDefault(IIndicatorDataSource.Cvm),
  statementType: statementTypeSchema.withDefault(IFinancialsStatementType.Annual),
  isPrimaryTickerSymbol: parseAsBoolean.withDefault(true).withOptions({ clearOnDefault: true }),
  input: runScreenerInputObjectIdSchema.withOptions({ clearOnDefault: true }),
  list: tickerSymbolListSchema.withOptions({ clearOnDefault: true }),
  sortBy: screenerSortSpecifierSchema.withOptions({
    clearOnDefault: true,
  }),
};

export type SetScreenerURLStateInput = UseQueryStatesReturn<typeof urlSearchParams>[0];
export type SetScreenerURLState = UseQueryStatesReturn<typeof urlSearchParams>[1];

interface Props {
  defaultRunScreenerInput?: NonNullable<IRunScreenerInputQuery["runScreenerInput"]> | null;
}
export type ScreenerURLState = {
  platformVertical: IPlatformVertical;
  primaryDataSource: IIndicatorDataSource;
  statementType: IFinancialsStatementType;
  isPrimaryTickerSymbol: boolean;
  storedRunInputId: string | null;
  filterByTickerSymbolList: ITickerSymbolList | null;
  sortSpecifier: IScreenerSortSpecifier | null;
};
export function useUrlSearchParams({ defaultRunScreenerInput }: Props) {
  const defaultInput = useInitialValue(defaultRunScreenerInput?.input as IRunScreenerInput);
  const [didTransferStateToUrl, setDidTransferStateToUrl] = useState(false);
  const [
    {
      vertical: platformVertical,
      primaryDataSource,
      statementType,
      isPrimaryTickerSymbol,
      input: storedRunInputId,
      list: filterByTickerSymbolList,
      sortBy: sortSpecifier,
    },
    setURLState,
  ] = useQueryStates(
    {
      vertical: platformVerticalSchema.withDefault(IPlatformVertical.Acoes),
      primaryDataSource: primaryDataSourceSchema.withDefault(IIndicatorDataSource.Cvm),
      statementType: statementTypeSchema.withDefault(IFinancialsStatementType.Annual),
      isPrimaryTickerSymbol: parseAsBoolean.withDefault(true).withOptions({ clearOnDefault: true }),
      input: runScreenerInputObjectIdSchema.withOptions({ clearOnDefault: true }),
      list: tickerSymbolListSchema.withOptions({ clearOnDefault: true }),
      sortBy: screenerSortSpecifierSchema.withOptions({
        clearOnDefault: true,
      }),
    },
    {
      clearOnDefault: true,
      startTransition,
      shallow: true,
      history: "replace",
    },
  );

  const isInitialMount = useMountedState();
  useEffectOnce(() => {
    if (isInitialMount() && defaultInput) {
      const { sortSpecifiers } = defaultInput;
      startTransition(() => {
        setURLState({
          sortBy: sortSpecifiers?.length ? sortSpecifiers[0] : null,
          list: defaultInput.filterByTickerSymbolList || null,
          isPrimaryTickerSymbol: defaultInput.isPrimaryTickerSymbol || false,
          statementType: defaultInput.statementType || IFinancialsStatementType.Annual,
          primaryDataSource: (defaultInput.primaryDataSource as PrimaryIndicatorDataSource) || IIndicatorDataSource.Cvm,
          vertical: platformVerticalSchema.parse(defaultInput.vertical),
        });
        setDidTransferStateToUrl(true);
      });
      return;
    }
    setDidTransferStateToUrl(true);
  });

  if (!didTransferStateToUrl && defaultInput) {
    const { sortSpecifiers } = defaultInput;
    return {
      setURLState,
      state: preProcessURLState({
        storedRunInputId: defaultRunScreenerInput?.objectId || null,
        sortSpecifier: sortSpecifiers?.length ? sortSpecifiers[0] : null,
        filterByTickerSymbolList: defaultInput.filterByTickerSymbolList || null,
        isPrimaryTickerSymbol: defaultInput.isPrimaryTickerSymbol || false,
        statementType: defaultInput.statementType || IFinancialsStatementType.Annual,
        primaryDataSource: (defaultInput.primaryDataSource as PrimaryIndicatorDataSource) || IIndicatorDataSource.Cvm,
        platformVertical: defaultInput.vertical,
      } satisfies ScreenerURLState),
    };
  }

  return {
    setURLState,
    state: preProcessURLState({
      platformVertical: platformVertical as IPlatformVertical,
      primaryDataSource,
      statementType,
      isPrimaryTickerSymbol,
      storedRunInputId,
      filterByTickerSymbolList,
      sortSpecifier,
    } satisfies ScreenerURLState),
  };
}

function preProcessURLState(state: ScreenerURLState): ScreenerURLState {
  if (state.platformVertical === IPlatformVertical.Stocks || state.platformVertical === IPlatformVertical.Reits) {
    return {
      ...state,
      primaryDataSource: IIndicatorDataSource.Morningstar,
    };
  }
  if (state.platformVertical === IPlatformVertical.Fiis) {
    return {
      ...state,
      primaryDataSource: IIndicatorDataSource.Fnet,
      statementType: IFinancialsStatementType.Monthly,
    };
  }
  if (state.platformVertical === IPlatformVertical.Acoes) {
    return {
      ...state,
      primaryDataSource: IIndicatorDataSource.Cvm,
    };
  }
  return state;
}

export const loadSearchParams = createLoader(urlSearchParams);

function useInitialValue<T>(value: T) {
  const initialValueRef = useRef<T | null>(value);
  useEffectOnce(() => {
    initialValueRef.current = null;
  });
  return initialValueRef.current;
}

export type ExactPlatformVerticals = Extract<
  IPlatformVertical,
  IPlatformVertical.Acoes | IPlatformVertical.Fiis | IPlatformVertical.Stocks | IPlatformVertical.Reits
>;

export type StoredRunScreenerInput = Omit<IRunScreenerInputsQuery["runScreenerInputs"][number], "input"> & {
  input: IRunScreenerInput;
};
export function deriveUrlStateFromInput(rsi: StoredRunScreenerInput): SetScreenerURLStateInput {
  return {
    ...makeEmptyScreenerUrlState(),
    input: rsi.objectId,
    vertical: platformVerticalSchema.parse(rsi.input.vertical)!,
    primaryDataSource: primaryDataSourceSchema.parse(rsi.input.primaryDataSource)!,
    isPrimaryTickerSymbol: rsi.input.isPrimaryTickerSymbol || false,
    statementType: rsi.input.statementType,
    list: rsi.input.filterByTickerSymbolList || null,
    sortBy: rsi.input.sortSpecifiers?.length ? rsi.input.sortSpecifiers[0] : null,
  };
}

export function makeEmptyScreenerUrlState(
  vertical?: ExactPlatformVerticals,
  primaryDataSource?: IIndicatorDataSource | null,
) {
  return {
    input: null,
    vertical: vertical || null,
    primaryDataSource: primaryDataSource ? primaryDataSourceSchema.parse(primaryDataSource) : null,
    isPrimaryTickerSymbol: null,
    statementType: null,
    list: null,
    sortBy: null,
  };
}
